import React, { useEffect, useState } from 'react'
import { PageLink, PageTitle } from '../../../../_metronic/layout/core'
import { getFeaturesConfigApps, getAppFeaturesConfig, updateAppFeaturesConfig } from '../../../../_metronic/helpers/backend_helper'
import { AppList } from '../components'
import { Parameter, Feature, AppData } from '../interfaces'
import './AdminFeatures.scss'
import { toast } from 'react-toastify'

const prettifyFeatureKey = (featureKey: string): string => {
  return featureKey
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Available platforms for selection
const availablePlatforms = ['ios', 'android', 'unity_desktop', 'web_gl']

const AdminFeatures: React.FC = () => {
  const [apps, setApps] = useState<[]>([])
  const [platforms, setPlatforms] = useState<string[]>([])
  const [selectedAppName, setSelectedAppName] = useState<string>('')
  const [selectedAppToken, setSelectedAppToken] = useState<string>('')
  const [selectedPlatform, setSelectedPlatform] = useState<string>('')
  const [selectedAppData, setSelectedAppData] = useState<AppData>({})
  const [selectedPlatformData, setSelectedPlatformData] = useState<{ [key: string]: Feature }>({})

  const [isLoadingApps, setIsLoadingApps] = useState<boolean>(true)
  const [isLoadingAppData, setIsLoadingAppData] = useState<boolean>(false)

  const [searchTerm, setSearchTerm] = useState<string>('')
  const [collapsedCards, setCollapsedCards] = useState<Set<string>>(new Set())
  const [editingFeature, setEditingFeature] = useState<string | null>(null)
  const [newParameter, setNewParameter] = useState<Partial<Parameter>>({})
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  // New platform management states
  const [showAddPlatformModal, setShowAddPlatformModal] = useState<boolean>(false)
  const [newPlatformName, setNewPlatformName] = useState<string>('')
  const [isAddingPlatform, setIsAddingPlatform] = useState<boolean>(false)
  const [copyFromPlatform, setCopyFromPlatform] = useState<string>('')

  // Track original data and newly added items so that we can allow the user to remove the platform/parameter if they want to
  const [newlyAddedPlatforms, setNewlyAddedPlatforms] = useState<Set<string>>(new Set())
  const [newlyAddedFeatures, setNewlyAddedFeatures] = useState<Set<string>>(new Set())
  const [newlyAddedParameters, setNewlyAddedParameters] = useState<Map<string, Set<string>>>(new Map()) // featureKey -> Set<paramKey>

  const [pendingFeatureKey, setPendingFeatureKey] = useState<string>('')
  const [editingFeatureKey, setEditingFeatureKey] = useState<string>('')
  const [featureKeyError, setFeatureKeyError] = useState<string>('')
  const [parameterKeyError, setParameterKeyError] = useState<string>('')
  const [platformNameError, setPlatformNameError] = useState<string>('')

  /**
     * Checks if a feature key already exists in the current platform's data.
     * Optionally excludes a specific key (e.g., when editing an existing feature).
     * Returns true if a duplicate is found, false otherwise.
     *
     * @param featureKey - The feature key to check for duplication.
     * @param excludeCurrentKey - (Optional) A feature key to exclude from the check (e.g., the current key being edited).
     */
  const isFeatureKeyDuplicate = (featureKey: string, excludeCurrentKey?: string): boolean => {
    if (!selectedPlatformData) return false

    const normalizedKey = featureKey.trim().toLowerCase()
    const excludeKey = excludeCurrentKey ? excludeCurrentKey.trim().toLowerCase() : null

    return Object.keys(selectedPlatformData).some(key => {
      const normalizedExistingKey = key.trim().toLowerCase()
      return normalizedExistingKey === normalizedKey && normalizedExistingKey !== excludeKey
    })
  }

  /**
   * Checks if a parameter key already exists in the current feature's data.
   * Optionally excludes a specific key (e.g., when editing an existing parameter).
   * Returns true if a duplicate is found, false otherwise.
   *
   * @param featureKey - The feature key containing the parameter.
   * @param parameterKey - The parameter key to check for duplication.
   * @param excludeCurrentParamKey - (Optional) A parameter key to exclude from the check (e.g., the current key being edited).
   */
  const isParameterKeyDuplicate = (featureKey: string, parameterKey: string, excludeCurrentParamKey?: string): boolean => {
    if (!selectedPlatformData || !selectedPlatformData[featureKey]) return false

    const normalizedParamKey = parameterKey.trim().toLowerCase()
    const excludeParamKey = excludeCurrentParamKey ? excludeCurrentParamKey.trim().toLowerCase() : null

    return (selectedPlatformData[featureKey].params || []).some(param => {
      const normalizedExistingParamKey = param.key.trim().toLowerCase()
      return normalizedExistingParamKey === normalizedParamKey && normalizedExistingParamKey !== excludeParamKey
    })
  }

  /**
   * Checks if a platform name already exists in the current list of platforms.
   * Returns true if a duplicate is found, false otherwise.
   *
   * @param platformName - The platform name to check for duplication.
   */
  const isPlatformNameDuplicate = (platformName: string): boolean => {
    const normalizedName = platformName.trim().toLowerCase()
    return platforms.some(platform => platform.trim().toLowerCase() === normalizedName)
  }

  const getAvailablePlatformsForSelection = (): string[] => {
    return availablePlatforms.filter(platform => !platforms.includes(platform))
  }

  // Fetch apps on component mount
  useEffect(() => {
    const fetchApps = async () => {
      setIsLoadingApps(true)
      try {
        const data = await getFeaturesConfigApps();
        setApps(data);
      } catch (error: any) {
        console.error('Error fetching apps:', error);
      } finally {
        setIsLoadingApps(false)
      }
    };
    fetchApps();
  }, []);

  // Auto-select first platform when app selected
  useEffect(() => {
    if (platforms.length > 0 && selectedAppData && !selectedPlatform) {
      setSelectedPlatform(platforms[0]);
      const platformData = selectedAppData[platforms[0]] || {};
      setSelectedPlatformData(platformData);
      // Collapse all cards by default
      setCollapsedCards(new Set(Object.keys(platformData)))
    } else if (platforms.length === 0) {
      setSelectedPlatform('');
      setSelectedPlatformData({});
    }
  }, [selectedAppData, platforms, selectedPlatform]);

  const clearAllStates = () => {
    // Clear all states at the beginning to prevent cross-app contamination
    setSelectedAppData({});
    setPlatforms([]);
    setSelectedPlatform('');
    setSelectedPlatformData({});
    setCollapsedCards(new Set());
    setEditingFeature(null);
    setNewParameter({});
    setPendingFeatureKey('');
    setEditingFeatureKey('');
    setFeatureKeyError('');
    setParameterKeyError('');
    setPlatformNameError('');
    setSaveMessage(null);

    // Clear tracking states
    setNewlyAddedPlatforms(new Set());
    setNewlyAddedFeatures(new Set());
    setNewlyAddedParameters(new Map());
  }

  const onAppSelection = async (app_token: string, app_name: string) => {
    clearAllStates();

    const fetchAppConfigs = async (app_token: string, app_name: string) => {
      try {
        const data = await getAppFeaturesConfig(app_token, app_name);
        return data;
      } catch (error: any) {
        // Handle 404 case - return empty object if no data found
        if (error.status === 404) {
          return {};
        }
        console.error('Error fetching app configs:', error);
      }
    };

    setIsLoadingAppData(true)
    try {
      const appData = apps.find((a: any) => a.app_name == app_name) as any;
      if (!appData) {
        toast.error(`Selected app ${app_name} - ${app_token} not found in the list of loaded apps`);
        return;
      }

      let appConfig: any = await fetchAppConfigs(app_token, app_name);

      // Check if appConfig is empty or doesn't have features_config
      if (!appConfig || !appConfig.features_config || Object.keys(appConfig.features_config).length === 0) {
        setSelectedAppName(app_name);
        setSelectedAppToken(appData.app_token);
        return;
      }

      appConfig = appConfig.features_config;
      const platforms = Object.keys(appConfig); // platforms are keys inside app_name, e.g. "android", "ios", "web"
      const appPlatformsData = appConfig;

      setSelectedAppData(appPlatformsData);
      setPlatforms(platforms);
      setSelectedAppName(app_name);
      setSelectedAppToken(appData.app_token);
    } catch (error) {
      console.error('Error loading app data:', error);
    } finally {
      setIsLoadingAppData(false)
    }
  }

  // Helper function to update platform data and persist changes
  const updatePlatformDataAndPersist = (updatedPlatformData: { [key: string]: Feature }) => {
    setSelectedPlatformData(updatedPlatformData)

    // Update the app data to persist changes
    const updatedAppData = { ...selectedAppData }
    updatedAppData[selectedPlatform] = updatedPlatformData
    setSelectedAppData(updatedAppData)
  }

  const onPlatformSelection = (platform: string) => {
    // Create a working copy of the app data
    let workingAppData = { ...selectedAppData };

    // Safety mechanism: Ensure current platform data is persisted before switching
    // (This should already be done by updatePlatformDataAndPersist, but serves as a backup for edge cases)
    if (selectedPlatform && selectedPlatformData) {
      workingAppData[selectedPlatform] = { ...selectedPlatformData };
    }

    // Update the app data state
    setSelectedAppData(workingAppData);

    // Switch to the new platform using the working copy and normalize the data
    setSelectedPlatform(platform);
    const platformData = workingAppData[platform] || {};
    setSelectedPlatformData(platformData);
  }

  const toggleCardCollapse = (featureKey: string) => {
    const newCollapsedCards = new Set(collapsedCards)
    if (newCollapsedCards.has(featureKey)) {
      newCollapsedCards.delete(featureKey)
    } else {
      newCollapsedCards.add(featureKey)
    }
    setCollapsedCards(newCollapsedCards)
  }

  // Features management section
  const addNewFeature = () => {
    const featureKey = `new_feature_${Date.now()}`
    const updatedPlatformData = { ...selectedPlatformData }
    updatedPlatformData[featureKey] = {
      description: '',
      params: []
    }
    updatePlatformDataAndPersist(updatedPlatformData)

    // Track newly added feature
    setNewlyAddedFeatures(prev => new Set([...prev, featureKey]))

    setEditingFeature(featureKey)

    // Scroll to bottom after adding new feature
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth'
      })
    }, 100) // Small delay to ensure DOM is updated
  }

  const removeFeature = (featureKey: string) => {
    // Only allow removal of newly added features
    if (!newlyAddedFeatures.has(featureKey)) {
      setSaveMessage({
        type: 'error',
        text: `Cannot remove "${featureKey}" feature. Only newly added features can be removed.`
      })
      setTimeout(() => setSaveMessage(null), 3000)
      return
    }

    const updatedPlatformData = { ...selectedPlatformData }
    delete updatedPlatformData[featureKey]
    updatePlatformDataAndPersist(updatedPlatformData)

    // Remove from newly added features tracking
    setNewlyAddedFeatures(prev => {
      const newSet = new Set(prev)
      newSet.delete(featureKey)
      return newSet
    })

    // Remove from newly added parameters tracking
    setNewlyAddedParameters(prev => {
      const newMap = new Map(prev)
      newMap.delete(featureKey)
      return newMap
    })
  }

  const updateFeatureDescription = (featureKey: string, description: string) => {
    const updatedPlatformData = { ...selectedPlatformData }
    if (updatedPlatformData[featureKey]) {
      updatedPlatformData[featureKey].description = description
      updatePlatformDataAndPersist(updatedPlatformData)
    }
  }

  const updateFeatureKey = (oldKey: string, newKey: string) => {
    if (oldKey === newKey || !newKey.trim()) return

    // Check for duplicate feature key
    if (isFeatureKeyDuplicate(newKey, oldKey)) {
      setSaveMessage({
        type: 'error',
        text: `Feature "${newKey}" already exists in this platform. Feature keys must be unique within each platform.`
      })
      setTimeout(() => setSaveMessage(null), 3000)
      return
    }

    const updatedPlatformData = { ...selectedPlatformData }
    const feature = updatedPlatformData[oldKey]

    // Remove the old key and add with new key
    delete updatedPlatformData[oldKey]
    updatedPlatformData[newKey] = feature

    updatePlatformDataAndPersist(updatedPlatformData)

    // Update editing state if this feature was being edited
    if (editingFeature === oldKey) {
      setEditingFeature(newKey)
    }

    // Update tracking for newly added features and parameters
    syncNewTrackedFeatures(oldKey, newKey)
  }

  const syncNewTrackedFeatures = (oldKey: string, newKey: string) => {
    // Update newly added features tracking
    if (newlyAddedFeatures.has(oldKey)) {
      setNewlyAddedFeatures(prev => {
        const newSet = new Set(prev)
        newSet.delete(oldKey)
        newSet.add(newKey)
        return newSet
      })
    }

    // Update newly added parameters tracking
    setNewlyAddedParameters(prev => {
      const newMap = new Map(prev)
      if (newMap.has(oldKey)) {
        newMap.set(newKey, newMap.get(oldKey)!)
        newMap.delete(oldKey)
      }
      return newMap
    })
  }

  const handleFeatureKeyChange = (oldKey: string, newKey: string) => {
    setPendingFeatureKey(newKey)

    // Clear error if user is typing
    if (featureKeyError) {
      setFeatureKeyError('')
    }

    // Real-time validation
    if (newKey.trim() && isFeatureKeyDuplicate(newKey, oldKey)) {
      setFeatureKeyError(`Feature "${newKey}" already exists in this platform.`)
    } else {
      setFeatureKeyError('')
    }
  }

  const applyFeatureKeyChange = (oldKey: string) => {
    if (pendingFeatureKey && pendingFeatureKey !== oldKey && pendingFeatureKey.trim()) {
      // Check for validation errors before applying
      if (featureKeyError) {
        setSaveMessage({
          type: 'error',
          text: featureKeyError
        })
        setTimeout(() => setSaveMessage(null), 3000)
        return
      }

      updateFeatureKey(oldKey, pendingFeatureKey.trim())
    }
    setPendingFeatureKey('')
    setEditingFeatureKey('')
    setFeatureKeyError('')
  }

  const startEditingFeature = (featureKey: string) => {
    setEditingFeature(featureKey)
    setNewParameter({})
    setFeatureKeyError('')
    setParameterKeyError('')
    setPendingFeatureKey('')
    setEditingFeatureKey('')
  }

  const cancelEditing = () => {
    setEditingFeature(null)
    setNewParameter({})
    setFeatureKeyError('')
    setParameterKeyError('')
    setPendingFeatureKey('')
    setEditingFeatureKey('')
  }

  const addParameter = (featureKey: string) => {
    if (!newParameter.key || !newParameter.type) {
      return
    }

    // Check for duplicate parameter key
    if (isParameterKeyDuplicate(featureKey, newParameter.key)) {
      setSaveMessage({
        type: 'error',
        text: `Parameter "${newParameter.key}" already exists in this feature. Parameter keys must be unique within each feature.`
      })
      setTimeout(() => setSaveMessage(null), 3000)
      return
    }

    const updatedPlatformData = { ...selectedPlatformData }
    if (!updatedPlatformData[featureKey]) {
      updatedPlatformData[featureKey] = { params: [] }
    }

    // Ensure params is an array
    if (!updatedPlatformData[featureKey].params) {
      updatedPlatformData[featureKey].params = []
    }

    const parameter: Parameter = {
      key: newParameter.key,
      type: newParameter.type as 'number' | 'integer' | 'text',
      min: newParameter.min,
      max: newParameter.max,
    }

    updatedPlatformData[featureKey].params.push(parameter)
    updatePlatformDataAndPersist(updatedPlatformData)

    // Track newly added parameter
    setNewlyAddedParameters(prev => {
      const newMap = new Map(prev)
      if (!newMap.has(featureKey)) {
        newMap.set(featureKey, new Set())
      }
      newMap.get(featureKey)!.add(parameter.key)
      return newMap
    })

    setNewParameter({})
    setParameterKeyError('')
  }

  const removeParameter = (featureKey: string, paramIndex: number) => {
    const updatedPlatformData = { ...selectedPlatformData }
    if (updatedPlatformData[featureKey]?.params) {
      const parameter = updatedPlatformData[featureKey].params[paramIndex]

      // Check if this parameter is newly added
      const isNewlyAdded = newlyAddedParameters.get(featureKey)?.has(parameter.key)

      if (!isNewlyAdded) {
        setSaveMessage({
          type: 'error',
          text: `Cannot remove "${parameter.key}" parameter. Only newly added parameters can be removed.`
        })
        setTimeout(() => setSaveMessage(null), 3000)
        return
      }

      updatedPlatformData[featureKey].params.splice(paramIndex, 1)
      updatePlatformDataAndPersist(updatedPlatformData)

      // Remove from newly added parameters tracking
      setNewlyAddedParameters(prev => {
        const newMap = new Map(prev)
        const featureParams = newMap.get(featureKey)
        if (featureParams) {
          featureParams.delete(parameter.key)
          if (featureParams.size === 0) {
            newMap.delete(featureKey)
          }
        }
        return newMap
      })
    }
  }

  const updateParameter = (featureKey: string, paramIndex: number, field: keyof Parameter, value: any) => {
    const updatedPlatformData = { ...selectedPlatformData }
    if (updatedPlatformData[featureKey]?.params && updatedPlatformData[featureKey].params[paramIndex]) {
      const currentParam = updatedPlatformData[featureKey].params[paramIndex]

      // If updating the key field, check for duplicates
      if (field === 'key' && value !== currentParam.key) {
        if (isParameterKeyDuplicate(featureKey, value, currentParam.key)) {
          setSaveMessage({
            type: 'error',
            text: `Parameter "${value}" already exists in this feature. Parameter keys must be unique within each feature.`
          })
          setTimeout(() => setSaveMessage(null), 3000)
          return
        }
      }

      updatedPlatformData[featureKey].params[paramIndex] = {
        ...updatedPlatformData[featureKey].params[paramIndex],
        [field]: value
      }
      updatePlatformDataAndPersist(updatedPlatformData)

      // Update tracking for newly added parameters if key was changed
      if (field === 'key' && value !== currentParam.key) {
        syncNewTrackedParameters(featureKey, currentParam.key, value)
      }
    }
  }

  const syncNewTrackedParameters = (featureKey: string, oldParamKey: string, newParamKey: string) => {
    // Update newly added parameters tracking
    setNewlyAddedParameters(prev => {
      const newMap = new Map(prev)
      const featureParams = newMap.get(featureKey)
      if (featureParams && featureParams.has(oldParamKey)) {
        featureParams.delete(oldParamKey)
        featureParams.add(newParamKey)
      }
      return newMap
    })
  }


  const handleParameterKeyChange = (featureKey: string, newKey: string) => {
    // Clear error if user is typing
    if (parameterKeyError) {
      setParameterKeyError('')
    }

    // Real-time validation
    if (newKey.trim() && isParameterKeyDuplicate(featureKey, newKey)) {
      setParameterKeyError(`Parameter "${newKey}" already exists in this feature.`)
    } else {
      setParameterKeyError('')
    }
  }

  const handlePlatformNameChange = (newName: string) => {
    // Clear error if user is typing
    if (platformNameError) {
      setPlatformNameError('')
    }

    // Real-time validation
    if (newName.trim() && isPlatformNameDuplicate(newName)) {
      setPlatformNameError(`Platform "${newName}" already exists.`)
    } else {
      setPlatformNameError('')
    }
  }

  const handleAddPlatform = () => {
    if (!newPlatformName) return

    const platformName = newPlatformName.toLowerCase()

    // Check if platform already exists
    if (isPlatformNameDuplicate(platformName)) {
      setSaveMessage({
        type: 'error',
        text: `Platform "${platformName}" already exists. Platform names must be unique.`
      })
      setTimeout(() => setSaveMessage(null), 3000)
      return
    }

    setIsAddingPlatform(true)

    try {
      // Create new platform data
      const newPlatformData: { [key: string]: Feature } = {}

      // If copying from another platform, copy its features
      if (copyFromPlatform && selectedAppData[copyFromPlatform]) {
        Object.keys(selectedAppData[copyFromPlatform]).forEach(featureKey => {
          const feature = selectedAppData[copyFromPlatform][featureKey]
          newPlatformData[featureKey] = {
            description: (feature as any).description || '',
            params: Array.isArray(feature.params) ? [...feature.params] : [] // Deep copy parameters with safety check
          }
        })
      }

      // Update app data with new platform
      const updatedAppData = { ...selectedAppData }
      updatedAppData[platformName] = newPlatformData
      setSelectedAppData(updatedAppData)

      // Update platforms list
      const updatedPlatforms = [...platforms, platformName]
      setPlatforms(updatedPlatforms)

      // Track newly added platform
      setNewlyAddedPlatforms(prev => new Set([...prev, platformName]))

      // Select the new platform
      setSelectedPlatform(platformName)
      setSelectedPlatformData(newPlatformData)

      // Reset form
      setNewPlatformName('')
      setCopyFromPlatform('')
      setPlatformNameError('')
      setShowAddPlatformModal(false)

      setSaveMessage({
        type: 'success',
        text: `Platform "${platformName}" added successfully.`
      })

      // Clear success message after 3 seconds
      setTimeout(() => setSaveMessage(null), 3000)

    } catch (error) {
      console.error('Error adding platform:', error)
      setSaveMessage({
        type: 'error',
        text: 'Failed to add platform. Please try again.'
      })
    } finally {
      setIsAddingPlatform(false)
    }
  }

  const handleRemovePlatform = (platformName: string) => {
    // Only allow removal of newly added platforms
    if (!newlyAddedPlatforms.has(platformName)) {
      setSaveMessage({
        type: 'error',
        text: `Cannot remove "${platformName}" platform. Only newly added platforms can be removed.`
      })
      setTimeout(() => setSaveMessage(null), 3000)
      return
    }

    if (!confirm(`Are you sure you want to remove the "${platformName}" platform? This action cannot be undone.`)) {
      return
    }

    try {
      // Remove platform from app data
      const updatedAppData = { ...selectedAppData }
      delete updatedAppData[platformName]
      setSelectedAppData(updatedAppData)

      // Update platforms list
      const updatedPlatforms = platforms.filter(p => p !== platformName)
      setPlatforms(updatedPlatforms)

      // Remove from newly added platforms tracking
      setNewlyAddedPlatforms(prev => {
        const newSet = new Set(prev)
        newSet.delete(platformName)
        return newSet
      })

      // If the removed platform was selected, select the first available platform
      if (selectedPlatform === platformName) {
        if (updatedPlatforms.length > 0) {
          setSelectedPlatform(updatedPlatforms[0])
          const platformData = updatedAppData[updatedPlatforms[0]] || {}
          setSelectedPlatformData(platformData)
        } else {
          setSelectedPlatform('')
          setSelectedPlatformData({})
        }
      }

      setSaveMessage({
        type: 'success',
        text: `Platform "${platformName}" removed successfully.`
      })

      // Clear success message after 3 seconds
      setTimeout(() => setSaveMessage(null), 3000)

    } catch (error) {
      console.error('Error removing platform:', error)
      setSaveMessage({
        type: 'error',
        text: 'Failed to remove platform. Please try again.'
      })
    }
  }

  const handleSave = async () => {
    // Check for validation errors before saving
    if (featureKeyError || parameterKeyError || platformNameError) {
      setSaveMessage({
        type: 'error',
        text: 'Cannot save changes. Please fix all validation errors before saving.'
      })
      setTimeout(() => setSaveMessage(null), 3000)
      return
    }

    // Check for any pending feature key changes that might have validation errors
    if (pendingFeatureKey && pendingFeatureKey.trim() && isFeatureKeyDuplicate(pendingFeatureKey, editingFeatureKey)) {
      setSaveMessage({
        type: 'error',
        text: 'Cannot save changes. Please fix the feature key validation error before saving.'
      })
      setTimeout(() => setSaveMessage(null), 3000)
      return
    }

    setIsSaving(true)
    setSaveMessage(null)

    try {
      // Ensure current platform data is saved before creating the final structure
      let finalAppData = { ...selectedAppData };
      if (selectedPlatform && selectedPlatformData) {
        finalAppData[selectedPlatform] = { ...selectedPlatformData };
      }


      const req_body = {
        features_config: finalAppData,
        version: 1
      }

      // Send put request to update or create the app configs
      await updateAppFeaturesConfig(selectedAppToken, selectedAppName, req_body);

      // Update the success message to reflect all platforms
      const platformCount = Object.keys(finalAppData).length;
      const platformList = Object.keys(finalAppData).join(', ');

      setSaveMessage({
        type: 'success',
        text: `Configuration saved successfully for ${selectedAppName} with ${platformCount} platform(s): ${platformList}.`
      })

      // Clear newly added tracking states after successful save
      setNewlyAddedPlatforms(new Set())
      setNewlyAddedFeatures(new Set())
      setNewlyAddedParameters(new Map())


      // Clear success message after 5 seconds
      setTimeout(() => setSaveMessage(null), 5000)
    } catch (error) {
      console.error('Error saving configuration:', error)
      setSaveMessage({
        type: 'error',
        text: 'Failed to save configuration. Please try again.'
      })
    } finally {
      setIsSaving(false)
    }
  }

  const configBreadcrumbs: Array<PageLink> = [
    {
      title: 'Home /',
      path: '/dashboard/apps',
      isSeparator: false,
      isActive: false,
    },
  ]

  return (
    <>
      <PageTitle breadcrumbs={configBreadcrumbs}>Admin Features</PageTitle>

      <div className="d-flex h-100 features-container" style={{ minHeight: 'calc(95vh - 200px)' }}>
        {/* Left Panel - Apps List */}
        <AppList
          apps={apps}
          selectedApp={selectedAppName}
          isLoadingApps={isLoadingApps}
          searchTerm={searchTerm}
          onAppSelection={onAppSelection}
          onSearchChange={setSearchTerm}
          onClearSearch={() => setSearchTerm('')}
        />

        {/* Right Panel - Configuration Editor */}
        {selectedAppName && selectedAppName.length > 0 ? (
          <div className="w-75 config-panel">
            <div className="p-4">
              {isLoadingAppData ? (
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mb-0">Loading configuration...</p>
                </div>
              ) : (
                <>
                  {/* Platform Tabs */}
                  <div className="platform-tabs mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h6 className="text-muted mb-0">Select Platform:</h6>
                        <small className="text-muted">
                          <i className="bi bi-info-circle me-1"></i>
                          Only newly added platforms, features, and parameters can be removed
                        </small>
                      </div>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setShowAddPlatformModal(true)}
                        title="Add New Platform"
                        disabled={getAvailablePlatformsForSelection().length === 0}
                      >
                        <i className="bi bi-plus-circle me-1"></i>
                        Add Platform
                        {getAvailablePlatformsForSelection().length === 0 && (
                          <span className="badge bg-secondary ms-1">All Added</span>
                        )}
                      </button>
                    </div>
                    {platforms.length > 0 ? (
                      <div className="d-flex gap-2 flex-wrap">
                        {platforms?.map((platform) => (
                          <div key={platform} className="position-relative">
                            <button
                              className={`platform-tab btn px-4 py-2 ${selectedPlatform === platform
                                ? 'platform-tab-active'
                                : 'platform-tab-inactive'
                                }`}
                              onClick={() => onPlatformSelection(platform)}
                              style={{ textTransform: 'capitalize' }}
                            >
                              {platform}
                              {newlyAddedPlatforms.has(platform) && (
                                <span className="badge bg-success ms-1" style={{ fontSize: '0.6rem' }}>New</span>
                              )}
                            </button>
                            {platforms.length >= 1 && newlyAddedPlatforms.has(platform) && (
                              <button
                                className="btn btn-sm btn-outline-danger position-absolute"
                                style={{
                                  top: '-8px',
                                  right: '-8px',
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '50%',
                                  padding: '0',
                                  fontSize: '10px'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleRemovePlatform(platform)
                                }}
                                title={`Remove ${platform} platform`}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-3 border rounded bg-light">
                        <i className="bi bi-info-circle text-muted mb-2"></i>
                        <p className="text-muted mb-2">No platforms configured yet. Add your first platform to get started.</p>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => setShowAddPlatformModal(true)}
                          disabled={getAvailablePlatformsForSelection().length === 0}
                        >
                          <i className="bi bi-plus-circle me-1"></i>
                          Add First Platform
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Configuration Editor */}
                  <div className="config-form-container">
                    <div className="config-form-header mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="fw-bold text-dark mb-2">Multi-Platform Parameter Management</h5>
                          <p className="text-muted mb-0">
                            {selectedPlatform
                              ? `Add, edit, or remove parameters for ${selectedPlatform} platform. Switch between platforms using the tabs above.`
                              : 'Select a platform from above to manage its features and parameters.'
                            }
                          </p>
                        </div>
                        {selectedPlatform && (
                          <button
                            className="btn btn-success"
                            onClick={addNewFeature}
                          >
                            <i className="bi bi-plus-circle me-2"></i>
                            Add New Feature
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="config-form-wrapper">
                      {!selectedPlatform ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px', width: '100%' }}>
                          <div className="text-center text-muted">
                            <i className="bi bi-arrow-up-circle fs-1 mb-3"></i>
                            <h5>No Platform Selected</h5>
                            <p>Please add a platform from above to start managing features and parameters.</p>
                          </div>
                        </div>
                      ) : selectedPlatformData && Object.keys(selectedPlatformData).length > 0 ? (
                        <div className="features-grid">
                          {Object.entries(selectedPlatformData).map(([featureKey, feature]) => (
                            <div key={featureKey} className="feature-card card mb-4">
                              <div className="card-header d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center flex-grow-1">
                                  {editingFeature === featureKey ? (
                                    <div className="d-flex align-items-center me-3">
                                      <div className="me-2">
                                        <label className="form-label small mb-1">Feature Key</label>
                                        <input
                                          type="text"
                                          className={`form-control form-control-sm ${featureKeyError ? 'is-invalid' : ''}`}
                                          value={editingFeatureKey === featureKey ? pendingFeatureKey : featureKey}
                                          onChange={(e) => handleFeatureKeyChange(featureKey, e.target.value)}
                                          onFocus={() => setEditingFeatureKey(featureKey)}
                                          onBlur={() => applyFeatureKeyChange(featureKey)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              applyFeatureKeyChange(featureKey)
                                            }
                                          }}
                                          style={{ minWidth: '150px' }}
                                          placeholder="e.g., median_xy"
                                        />
                                        {featureKeyError && editingFeatureKey === featureKey && (
                                          <div className="invalid-feedback" style={{ fontSize: '0.75rem' }}>
                                            {featureKeyError}
                                          </div>
                                        )}
                                      </div>
                                      <div>
                                        <label className="form-label small mb-1">Description</label>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={feature.description || ''}
                                          onChange={(e) => updateFeatureDescription(featureKey, e.target.value)}
                                          style={{ minWidth: '200px' }}
                                          placeholder="e.g., Detects median XY coordinates"
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="me-3">
                                      <h6 className="fw-bold mb-0">
                                        {prettifyFeatureKey(featureKey)}
                                        {newlyAddedFeatures.has(featureKey) && (
                                          <span className="badge bg-success ms-2" style={{ fontSize: '0.6rem' }}>New</span>
                                        )}
                                      </h6>
                                      <small className="text-muted">{featureKey}</small>
                                      {feature.description && (
                                        <div className="mt-1">
                                          <small className="text-muted">{feature.description}</small>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  <span className="badge bg-secondary">{(feature.params || []).length} params</span>
                                </div>
                                <div className="d-flex gap-2">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => editingFeature === featureKey ? cancelEditing() : startEditingFeature(featureKey)}
                                  >
                                    <i className={`bi bi-${editingFeature === featureKey ? 'x' : 'pencil'}`}></i>
                                  </button>
                                  {newlyAddedFeatures.has(featureKey) && (
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeFeature(featureKey)}
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-link p-0 text-muted"
                                    onClick={() => toggleCardCollapse(featureKey)}
                                    title={collapsedCards.has(featureKey) ? "Expand" : "Collapse"}
                                  >
                                    <i className={`bi bi-chevron-${collapsedCards.has(featureKey) ? 'right' : 'down'} fs-5`}></i>
                                  </button>
                                </div>
                              </div>
                              {!collapsedCards.has(featureKey) && (
                                <div className="card-body">
                                  {/* Existing Parameters */}
                                  {(feature.params || []).length > 0 && (
                                    <div className="mb-4">
                                      <h6 className="fw-semibold mb-3">Existing Parameters</h6>
                                      <div className="row">
                                        {(feature.params || []).map((param, paramIndex) => (
                                          <div key={param.key} className="col-md-6 mb-3">
                                            <div className="parameter-item border rounded p-3">
                                              <div className="d-flex justify-content-between align-items-start mb-2">
                                                <label className="form-label fw-semibold mb-1">
                                                  {param.key}
                                                  {newlyAddedParameters.get(featureKey)?.has(param.key) && (
                                                    <span className="badge bg-success ms-2" style={{ fontSize: '0.5rem' }}>New</span>
                                                  )}
                                                </label>
                                                {newlyAddedParameters.get(featureKey)?.has(param.key) && (
                                                  <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => removeParameter(featureKey, paramIndex)}
                                                  >
                                                    <i className="bi bi-trash"></i>
                                                  </button>
                                                )}
                                              </div>
                                              <div className="row g-2">
                                                <div className="col-6">
                                                  <label className="form-label small">Key</label>
                                                  <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={param.key}
                                                    onChange={(e) => updateParameter(featureKey, paramIndex, 'key', e.target.value)}
                                                  />
                                                </div>
                                                <div className="col-6">
                                                  <label className="form-label small">Type</label>
                                                  <select
                                                    className="form-select form-select-sm"
                                                    value={param.type}
                                                    onChange={(e) => updateParameter(featureKey, paramIndex, 'type', e.target.value)}
                                                  >
                                                    <option value="number">Number</option>
                                                    <option value="integer">Integer</option>
                                                    <option value="text">Text</option>
                                                  </select>
                                                </div>
                                                <div className="col-6">
                                                  <label className="form-label small">Min Value</label>
                                                  <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    value={param.min !== undefined ? param.min : ''}
                                                    onChange={(e) => updateParameter(featureKey, paramIndex, 'min', e.target.value ? parseFloat(e.target.value) : undefined)}
                                                  />
                                                </div>
                                                <div className="col-6">
                                                  <label className="form-label small">Max Value</label>
                                                  <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    value={param.max || ''}
                                                    onChange={(e) => updateParameter(featureKey, paramIndex, 'max', e.target.value ? parseFloat(e.target.value) : undefined)}
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Add New Parameter */}
                                  {editingFeature === featureKey && (
                                    <div className="border rounded p-3 bg-light">
                                      <h6 className="fw-semibold mb-3">Add New Parameter</h6>
                                      <div className="row g-3">
                                        <div className="col-md-6">
                                          <label className="form-label">Parameter Key</label>
                                          <input
                                            type="text"
                                            className={`form-control ${parameterKeyError ? 'is-invalid' : ''}`}
                                            value={newParameter.key || ''}
                                            onChange={(e) => {
                                              setNewParameter({ ...newParameter, key: e.target.value })
                                              handleParameterKeyChange(featureKey, e.target.value)
                                            }}
                                            placeholder="e.g., threshold"
                                          />
                                          {parameterKeyError && (
                                            <div className="invalid-feedback">
                                              {parameterKeyError}
                                            </div>
                                          )}
                                        </div>
                                        <div className="col-md-4">
                                          <label className="form-label">Type</label>
                                          <select
                                            className="form-select"
                                            value={newParameter.type || ''}
                                            onChange={(e) => setNewParameter({ ...newParameter, type: e.target.value as 'number' | 'integer' | 'text' })}
                                          >
                                            <option value="">Select Type</option>
                                            <option value="number">Number</option>
                                            <option value="integer">Integer</option>
                                            <option value="text">Text</option>
                                          </select>
                                        </div>
                                        <div className="col-md-4">
                                          <label className="form-label">Min Value</label>
                                          <input
                                            type="number"
                                            className="form-control"
                                            value={newParameter.min !== undefined ? newParameter.min : ''}
                                            onChange={(e) => setNewParameter({ ...newParameter, min: e.target.value ? parseFloat(e.target.value) : undefined })}
                                          />
                                        </div>
                                        <div className="col-md-4">
                                          <label className="form-label">Max Value</label>
                                          <input
                                            type="number"
                                            className="form-control"
                                            value={newParameter.max || ''}
                                            onChange={(e) => setNewParameter({ ...newParameter, max: e.target.value ? parseFloat(e.target.value) : undefined })}
                                          />
                                        </div>
                                        <div className="col-12">
                                          <button
                                            className="btn btn-primary"
                                            onClick={() => addParameter(featureKey)}
                                            disabled={!newParameter.key || !newParameter.type || !!parameterKeyError}
                                          >
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Add Parameter
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {(feature.params || []).length === 0 && editingFeature !== featureKey && (
                                    <div className="text-center py-4">
                                      <i className="bi bi-plus-circle text-muted fs-1 mb-3"></i>
                                      <p className="text-muted mb-0">No parameters defined for this feature.</p>
                                      <p className="text-muted">Click the edit button to add parameters.</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px', width: '100%' }}>
                          <div className="text-center text-muted">
                            {platforms.length === 0 ? (
                              <>
                                <i className="bi bi-plus-circle fs-1 mb-3 text-primary"></i>
                                <h5>No Platforms Available</h5>
                                <p className="mb-3">This app doesn't have any platforms configured yet.</p>
                                <button
                                  className="btn btn-primary"
                                  onClick={() => setShowAddPlatformModal(true)}
                                  disabled={getAvailablePlatformsForSelection().length === 0}
                                >
                                  <i className="bi bi-plus-circle me-2"></i>
                                  Add Your First Platform
                                </button>
                              </>
                            ) : (
                              <>
                                <i className="bi bi-arrow-left-circle fs-1 mb-3"></i>
                                <h5>No Features Available</h5>
                                <p>Please select another platform from the top or add new features</p>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Save Button and Status */}
                      {platforms.length > 0 && (
                        <div className="form-actions mt-4 pt-4 border-top">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="save-status">
                              {saveMessage && (
                                <div className={`alert alert-${saveMessage.type === 'success' ? 'success' : 'danger'} mb-0`}>
                                  {saveMessage.text}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              className="btn btn-primary btn-lg save-btn"
                              onClick={handleSave}
                              disabled={isSaving || !!featureKeyError || !!parameterKeyError || !!platformNameError || !!(pendingFeatureKey && pendingFeatureKey.trim() && isFeatureKeyDuplicate(pendingFeatureKey, editingFeatureKey))}
                            >
                              {isSaving ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Saving All Platforms...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-check-circle me-2"></i>
                                  Save
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="w-75 d-flex align-items-center justify-content-center">
            <div className="text-center text-muted">
              <i className="bi bi-arrow-left-circle fs-1 mb-3"></i>
              <h5>No App Selected</h5>
              <p>Please select an app from the left panel to manage parameters</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Platform Modal */}
      {showAddPlatformModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Platform</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddPlatformModal(false)
                    setNewPlatformName('')
                    setCopyFromPlatform('')
                    setPlatformNameError('')
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Platform Name</label>
                  <select
                    className={`form-select ${platformNameError ? 'is-invalid' : ''}`}
                    value={newPlatformName}
                    onChange={(e) => {
                      setNewPlatformName(e.target.value)
                      handlePlatformNameChange(e.target.value)
                    }}
                  >
                    <option value="">Select a platform</option>
                    {getAvailablePlatformsForSelection().map((platform) => (
                      <option key={platform} value={platform}>
                        {platform.charAt(0).toUpperCase() + platform.slice(1).replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                  {platformNameError && (
                    <div className="invalid-feedback">
                      {platformNameError}
                    </div>
                  )}
                  <div className="form-text">
                    Select a platform from the available options. Only platforms that haven't been added yet are shown.
                  </div>
                  {getAvailablePlatformsForSelection().length === 0 && (
                    <div className="alert alert-info mt-2">
                      <i className="bi bi-info-circle me-2"></i>
                      All available platforms have already been added to this app.
                    </div>
                  )}
                </div>

                {platforms.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">Copy Features From (Optional)</label>
                    <select
                      className="form-select"
                      value={copyFromPlatform}
                      onChange={(e) => setCopyFromPlatform(e.target.value)}
                    >
                      <option value="">Create empty platform</option>
                      {platforms.map((platform) => (
                        <option key={platform} value={platform}>
                          {platform.charAt(0).toUpperCase() + platform.slice(1)}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Select a platform to copy all features and parameters from
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddPlatformModal(false)
                    setNewPlatformName('')
                    setCopyFromPlatform('')
                    setPlatformNameError('')
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddPlatform}
                  disabled={!newPlatformName || isAddingPlatform || !!platformNameError}
                >
                  {isAddingPlatform ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Adding...
                    </>
                  ) : (
                    'Add Platform'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export { AdminFeatures } 